import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { PlanGoal } from '@/types/plan'

export interface GoalFormData {
  title: string
  description: string
  dueDate: string
  successCriteria: string
  skillTags: string[]
}

interface AddGoalFormProps {
  initialGoal?: PlanGoal
  onSubmit: (data: GoalFormData) => Promise<void> | void
  onCancel: () => void
}

const AddGoalForm: React.FC<AddGoalFormProps> = ({ initialGoal, onSubmit, onCancel }) => {
  const [title, setTitle] = useState(initialGoal?.title ?? '')
  const [description, setDescription] = useState(initialGoal?.description ?? '')
  const [dueDate, setDueDate] = useState(initialGoal ? initialGoal.dueDate.slice(0, 10) : '')
  const [successCriteria, setSuccessCriteria] = useState(initialGoal?.successCriteria ?? '')
  const [skillTags, setSkillTags] = useState<string[]>(initialGoal?.skillTags ?? [])
  const [tagInput, setTagInput] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const addTag = (raw: string) => {
    const tag = raw.trim().replace(/,$/, '').trim()
    if (tag && !skillTags.includes(tag)) {
      setSkillTags((prev) => [...prev, tag])
    }
    setTagInput('')
  }

  const handleTagInputChange = (value: string) => {
    if (value.endsWith(',')) {
      addTag(value)
    } else {
      setTagInput(value)
    }
  }

  const handleTagKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      addTag(tagInput)
    }
  }

  const removeTag = (tag: string) => {
    setSkillTags((prev) => prev.filter((t) => t !== tag))
  }

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {}
    if (!title.trim()) {
      nextErrors.title = 'Title is required'
    }
    if (!dueDate) {
      nextErrors.dueDate = 'Due date is required'
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!validate()) return

    try {
      setIsSubmitting(true)
      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        dueDate,
        successCriteria: successCriteria.trim(),
        skillTags: tagInput.trim() ? [...skillTags, tagInput.trim()] : skillTags
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 border rounded-lg p-4 bg-gray-50">
      <div>
        <label htmlFor="goal-title" className="block text-sm font-medium text-gray-700 mb-1">
          Title *
        </label>
        <input
          id="goal-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
      </div>

      <div>
        <label htmlFor="goal-description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="goal-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label htmlFor="goal-due-date" className="block text-sm font-medium text-gray-700 mb-1">
          Due date *
        </label>
        <input
          id="goal-due-date"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {errors.dueDate && <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>}
      </div>

      <div>
        <label htmlFor="goal-success-criteria" className="block text-sm font-medium text-gray-700 mb-1">
          Success criteria
        </label>
        <textarea
          id="goal-success-criteria"
          value={successCriteria}
          onChange={(e) => setSuccessCriteria(e.target.value)}
          rows={2}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label htmlFor="goal-skill-tags" className="block text-sm font-medium text-gray-700 mb-1">
          Skill tags
        </label>
        {skillTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {skillTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  aria-label={`Remove ${tag}`}
                  className="hover:text-gray-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <input
          id="goal-skill-tags"
          type="text"
          value={tagInput}
          onChange={(e) => handleTagInputChange(e.target.value)}
          onKeyDown={handleTagKeyDown}
          placeholder="Type a tag and press Enter or comma"
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save Goal'}
        </Button>
      </div>
    </form>
  )
}

export default AddGoalForm
